/**
 * Modal — the shell for "show me this record without leaving the page".
 *
 * It exists because the alternative kept costing the operator their place: a hexagon board or
 * an attendance timeline is a *position* (filters, expanded row, a camera you were watching),
 * and routing away to a detail page and back rebuilt the page from scratch — the browser's
 * Back button landed on the alert LIST, not on the board you came from. Anything that is a
 * detail *of* something on screen belongs in here instead of behind a route.
 *
 * Deliberately only a shell: it owns the overlay, the escape hatches and the scroll lock, and
 * nothing about what's inside. Detail content stays a plain component that also works as a
 * routed page, so neither copy can drift from the other.
 *
 * Escape hatches, all three of which people reach for: Escape, a click on the backdrop, and
 * the close button. `onClose` is called once by whichever fires.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'wide' | 'full';

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  // `wide` — a form with a summary column beside it (the alert rule editor).
  wide: 'max-w-6xl',
  // `full` — for records whose POINT is an image. A face crop, a number plate or a PPE
  // violation is what the operator opened the record to look at, and in a 2xl column it
  // arrives too small to judge; this size gives the panel the viewport minus the backdrop's
  // gutter, so the snapshot gets the width and the facts sit beside it instead of under it.
  full: 'max-w-none',
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  /** A small caps line above the title ("EDIT ALERT RULE"); the title is then shown larger. */
  eyebrow?: ReactNode;
  /**
   * The body is a flex column, so content with `flex-1 min-h-0` fills exactly the space between
   * header and footer — and can scroll in parts of its own (a form beside a summary).
   */
  fillBody?: boolean;
  /** Extra controls in the header, left of the close button (refresh, "open full page", …). */
  headerActions?: ReactNode;
  /** Pinned below the scrolling body — actions that must stay reachable in a long record. */
  footer?: ReactNode;
  size?: ModalSize;
  /** Accessible name when `title` isn't plain text. */
  ariaLabel?: string;
  children: ReactNode;
}

/** The open modals (and confirm dialogs), oldest first — Escape is for the last one only. */
const openModals: object[] = [];

/**
 * Join the Escape stack while `open`: Escape calls `onClose` only when this layer is the topmost
 * (a confirm dialog over a modal closes itself, not the modal underneath). `onClose` is read
 * fresh each time without moving the layer in the stack.
 */
export function useEscapeLayer(open: boolean, onClose: () => void) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const token = {};
    openModals.push(token);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openModals[openModals.length - 1] === token) {
        e.stopPropagation();
        closeRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const i = openModals.indexOf(token);
      if (i >= 0) openModals.splice(i, 1);
    };
  }, [open]);
}

export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  headerActions,
  footer,
  eyebrow,
  fillBody = false,
  size = 'lg',
  ariaLabel,
  children,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Escape closes. Bound on keydown at the document so it works before anything inside has
  // been focused — the operator clicked a card, not a form field. Only the TOPMOST open modal
  // closes: a dialog opened from another (a new contact group from the notification editor)
  // must not take the one underneath — and its unsaved work — with it.
  useEscapeLayer(open, onClose);

  // Lock the page behind the modal: without this, scrolling a long record scrolls the board
  // underneath once the panel hits its end, and the operator's position is lost anyway.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  // Move focus into the panel so keyboard users continue inside the dialog rather than
  // tabbing through the page they can no longer see. A field inside that already took focus
  // (`autoFocus` runs before this effect) keeps it — the dialog opened to be typed into.
  useEffect(() => {
    const panel = panelRef.current;
    if (open && panel && !panel.contains(document.activeElement)) panel.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overlay-backdrop p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || (typeof title === 'string' ? title : undefined)}
        tabIndex={-1}
        className={`flex ${size === 'full' ? 'max-h-[94vh]' : 'max-h-[90vh]'} w-full ${SIZE[size]} animate-modal-pop flex-col overflow-hidden rounded-xl border border-border bg-card shadow-modal outline-none`}
      >
        {(title || subtitle || headerActions) && (
          <div className={`flex shrink-0 gap-3 border-b border-border px-5 ${eyebrow ? 'items-center py-4' : 'items-start py-3.5'}`}>
            <div className="min-w-0 flex-1">
              {eyebrow && <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{eyebrow}</p>}
              {title && <h2 className={`truncate font-semibold text-foreground ${eyebrow ? 'text-[19px] leading-tight' : 'text-[15px]'}`}>{title}</h2>}
              {subtitle && <p className="mt-0.5 truncate text-[12px] text-muted-foreground">{subtitle}</p>}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {headerActions}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* The only scrolling region — the header and footer stay put in a long record. */}
        <div className={`min-h-0 flex-1 overflow-y-auto ${fillBody ? 'flex flex-col' : ''}`}>{children}</div>

        {footer && <div className="shrink-0 border-t border-border px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}
